import { useCallback, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Button,
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  Text,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
} from "@salestrak/ui";
import { useEditor, EditorContent } from "@tiptap/react";
import Document from "@tiptap/extension-document";
import Paragraph from "@tiptap/extension-paragraph";
import TextNode from "@tiptap/extension-text";
import Placeholder from "@tiptap/extension-placeholder";
import type { Extensions, JSONContent } from "@tiptap/core";

import { useSelectorKeyboardNav } from "#app/components/entity-selectors/hooks";
import { VariableToken, VARIABLE_TOKEN_NAME } from "./variable-token";
import {
  relativeDateLabel,
  type VariableGroup,
  type VariableItem,
} from "./automation-variables";
import "./variable-input.css";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

// Matches the backend's reference regex: `{{ path }}` with optional whitespace.
const VARIABLE_RE = /\{\{\s*([^}]+?)\s*\}\}/g;

// Split one line of stored text into inline nodes: literal text + variable
// tokens. Only paths the catalog knows (labelFor returns a label) become
// chips — an unknown reference stays literal `{{ path }}` text, matching the
// paste/typing rules, so nothing ever chips up that isn't a real variable.
function lineToInline(
  line: string,
  labelFor: (path: string) => string | null,
): JSONContent[] {
  const nodes: JSONContent[] = [];
  let last = 0;
  VARIABLE_RE.lastIndex = 0;
  let match: RegExpExecArray | null;
  while ((match = VARIABLE_RE.exec(line))) {
    const path = at(match, 1, "variable path").trim();
    const label = labelFor(path);
    if (label === null) continue;
    if (match.index > last) {
      nodes.push({ type: "text", text: line.slice(last, match.index) });
    }
    nodes.push({
      type: VARIABLE_TOKEN_NAME,
      attrs: { path, label },
    });
    last = match.index + match[0].length;
  }
  if (last < line.length) nodes.push({ type: "text", text: line.slice(last) });
  return nodes;
}

// Stored template string → editor doc. Newlines become paragraphs.
function parseToDoc(
  value: string,
  labelFor: (path: string) => string | null,
): JSONContent {
  const paragraphs = (value === "" ? [""] : value.split("\n")).map((line) => {
    const content = lineToInline(line, labelFor);
    return content.length
      ? { type: "paragraph", content }
      : { type: "paragraph" };
  });
  return { type: "doc", content: paragraphs };
}

// Editor doc → stored template string. Variable tokens re-emit `{{ path }}`;
// paragraphs join with newlines.
function nodeToText(node: JSONContent): string {
  if (node.type === VARIABLE_TOKEN_NAME) return `{{ ${node.attrs?.path} }}`;
  if (node.type === "text") return node.text ?? "";
  return (node.content ?? []).map(nodeToText).join("");
}
function docToString(doc: JSONContent): string {
  return (doc.content ?? []).map(nodeToText).join("\n");
}

const VARIABLE_POOL_ID = "variables";

// Never a persisted selection — every pick is an insert action, so the
// selector stays selection-less and the same variable can be inserted twice.
const NO_SELECTIONS: EntitySelection[] = [];

// The "Insert variable" picker — a SEARCHABLE dropdown over the categorized
// variable catalog, grouped under eyebrow headings in the builder's order:
// Project properties → Custom fields → Triggered by → each upstream step's
// outputs. One flat pool whose items carry `section`; search matches name,
// category, and dotted path at once. Reused by the chip editor below, the
// JSON editor, and the "Update field" value footer.
export function InsertVariableMenu({
  groups,
  onSelect,
  align = "end",
}: Omittable<{
  groups: VariableGroup[];
  onSelect: (item: VariableItem) => void;
  align?: "start" | "center" | "end";
}>) {
  const { ref: selectorRef, inputProps } = useSelectorKeyboardNav();

  // `id` = path (unique + stable); `section` = the source category, rendered
  // as an eyebrow heading over each group (and searchable via keywords, so
  // typing "custom" surfaces all custom fields). Upstream-step groups carry
  // the node's slug in the heading ("Create project · project-1") — the same
  // badge the canvas node shows on hover — so a group ties back to its node.
  const pool = useMemo<EntityPoolConfig>(
    () => ({
      id: VARIABLE_POOL_ID,
      label: "Variables",
      hideAvatars: true,
      items: groups.flatMap((g) => {
        const section = g.slug ? `${g.label} · ${g.slug}` : g.label;
        return g.items.map((item) => ({
          id: item.path,
          title: item.label,
          section,
          keywords: g.slug ? [g.label, g.slug] : [g.label],
          data: { item },
        }));
      }),
      // A workspace with many custom fields — plus one group per upstream step —
      // pushes the trigger's own context off screen, so each source category
      // folds away, the same disclosure as the add-step picker. Everything starts
      // OPEN (no `defaultCollapsedSections`): unlike that picker's provider list,
      // these groups are the point of the menu, and a first-run user must not
      // have to expand one to find anything.
      collapsibleSections: true,
    }),
    [groups],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 1,
      searchPlaceholder: "Search variables…",
      emptyMessage: "No variables found",
    }),
    [pool],
  );

  // Insert-and-close: the selection is consumed immediately (value stays
  // empty), never displayed on the trigger.
  const handleValueChange = useCallback(
    (next: EntitySelection[]) => {
      const item = next[0]?.entity.data?.item as VariableItem | undefined;
      if (item) onSelect(item);
    },
    [onSelect],
  );

  if (groups.length === 0) return null;

  return (
    <EntitySelector
      ref={selectorRef}
      config={config}
      value={NO_SELECTIONS}
      onValueChange={handleValueChange}
    >
      <EntitySelectorTrigger
        render={
          <Button variant="ghost" size="xs">
            <Icon name="plus" size="xs" />
            <Text size="xs" variant="muted">
              Insert variable
            </Text>
          </Button>
        }
      />
      <EntitySelectorContent align={align} size="sm">
        <EntitySelectorInput {...inputProps} />
        <EntitySelectorPool value={VARIABLE_POOL_ID} />
        <EntitySelectorEmpty />
      </EntitySelectorContent>
    </EntitySelector>
  );
}

export type VariableTextInputProps = Omittable<{
  value: string;
  onChange: (next: string) => void;
  groups: VariableGroup[];
  placeholder?: string;
  multiline?: boolean;
}>;

// A single/multi-line text field that renders `{{ }}` template references as
// friendly chips and inserts new ones from a catalog-driven menu — authors
// never type or see raw mustache syntax.
export function VariableTextInput({
  value,
  onChange,
  groups,
  placeholder,
  multiline = false,
}: VariableTextInputProps) {
  // Path → label for parsing stored values and validating pasted/typed refs.
  // Null = not an available variable here; the caller leaves it literal text.
  // Relative-date tokens ({{now}}, {{now±Nd}}) are always available — the
  // runner resolves them from the wall clock, independent of the groups.
  const labelFor = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) for (const v of g.items) map.set(v.path, v.label);
    return (path: string): string | null => map.get(path) ?? relativeDateLabel(path);
  }, [groups]);

  const labelForRef = useRef(labelFor);
  labelForRef.current = labelFor;

  const extensions = useMemo<Extensions>(
    () => [
      Document,
      Paragraph,
      TextNode,
      // The ref keeps the auto-chip plugin's label lookup current without
      // rebuilding the extension list when the variable catalog changes.
      VariableToken.configure({
        labelFor: (path: string) => labelForRef.current(path),
      }),
      Placeholder.configure({ placeholder: placeholder ?? "" }),
    ],
    [placeholder],
  );

  // Track the last value we emitted so the external-sync effect can tell our
  // own edits (skip — would reset the cursor) from a new selection (re-seed).
  const lastValueRef = useRef(value);

  const editor = useEditor({
    extensions,
    content: parseToDoc(value, labelForRef.current),
    editorProps: {
      attributes: { class: "focus:outline-none" },
      handleKeyDown: (_view, event) => {
        if (!multiline && event.key === "Enter") {
          event.preventDefault();
          return true;
        }
        return false;
      },
    },
    onUpdate: ({ editor: ed }) => {
      const text = docToString(ed.getJSON());
      lastValueRef.current = text;
      onChange(text);
    },
  });

  // Re-seed when the value changes from outside (switching steps), but not on
  // our own keystrokes.
  useEffect(() => {
    if (!editor) return;
    if (value === lastValueRef.current) return;
    lastValueRef.current = value;
    editor.commands.setContent(parseToDoc(value, labelForRef.current), {
      emitUpdate: false,
    });
  }, [value, editor]);

  // Apply the wrapper CSS class via ref (no raw className in app JSX).
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = wrapperRef.current as unknown as HTMLElement | null;
    if (el) {
      el.setAttribute(
        "class",
        multiline ? "variable-input variable-input-multiline" : "variable-input",
      );
    }
  }, [multiline]);

  const insertVariable = (item: VariableItem) => {
    editor
      ?.chain()
      .focus()
      .insertContent({
        type: VARIABLE_TOKEN_NAME,
        attrs: { path: item.path, label: item.label },
      })
      .run();
  };

  if (!editor) return null;

  return (
    <Box ref={wrapperRef} gap="xs">
      {/* Single-line matches the 40px `lg` control height every sibling
          value input uses; multiline grows from its CSS min-height. */}
      <Box
        border
        rounded="md"
        background="sunken"
        paddingX="sm"
        paddingY="xs"
        minHeight={multiline ? undefined : 40}
        justify={multiline ? undefined : "center"}
      >
        <EditorContent editor={editor} />
      </Box>
      {groups.length > 0 ? (
        <Box direction="row" justify="end">
          <InsertVariableMenu groups={groups} onSelect={insertVariable} />
        </Box>
      ) : null}
    </Box>
  );
}
